// MessageBubble.jsx - With audio play/stop controls
import React, { useState, useRef, useEffect } from 'react'
import PhotoPreview from './PhotoPreview'
import { linkPath, base_url, linkPathImageChat, domain } from '../../../share/helper'
import AutoPlayGallery from './AutoPlayGallery'
import AudioPlayer from './AudioPlayer'  // Add this at the top

export default function MessageBubble({ m, isMine }) {
  const BASE = (base_url ? String(base_url).replace(/\/api\/?$/i, '') : null) || (linkPath ? String(linkPath).replace(/\/photo\/.+$/, '') : domain)
  const IMAGE_CHAT_DOMAIN = linkPathImageChat ? String(linkPathImageChat).replace(/\/photo\/chat\/?$/, '') : null

  const [preview, setPreview] = useState(null)
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [galleryIndex, setGalleryIndex] = useState(0)
  const [galleryItems, setGalleryItems] = useState([])
  
  // 🔥 Audio player state
  const [playingAudioId, setPlayingAudioId] = useState(null)
  const [audioProgress, setAudioProgress] = useState(0)
  const [audioDuration, setAudioDuration] = useState(0)
  const audioRefs = useRef({})

  const FileIcon = ({ type }) => {
    if (type === 'pdf') return (
      <svg width="28" height="36" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="32" rx="2" fill="#E02F2F"/><text x="12" y="20" fontSize="10" fontWeight="700" textAnchor="middle" fill="#fff">PDF</text></svg>
    )
    if (type === 'doc') return (
      <svg width="28" height="36" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="32" rx="2" fill="#2B6CB0"/><text x="12" y="20" fontSize="10" fontWeight="700" textAnchor="middle" fill="#fff">DOC</text></svg>
    )
    if (type === 'xls') return (
      <svg width="28" height="36" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="32" rx="2" fill="#2F9A2F"/><text x="12" y="20" fontSize="10" fontWeight="700" textAnchor="middle" fill="#fff">XLS</text></svg>
    )
    if (type === 'ppt') return (
      <svg width="28" height="36" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="32" rx="2" fill="#D86C2F"/><text x="12" y="20" fontSize="10" fontWeight="700" textAnchor="middle" fill="#fff">PPT</text></svg>
    )
    if (type === 'heic') return (
      <svg width="28" height="36" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="32" rx="2" fill="#6B46C1"/><text x="12" y="20" fontSize="9" fontWeight="700" textAnchor="middle" fill="#fff">HEIC</text></svg>
    )
    return (
      <svg width="28" height="36" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="32" rx="2" fill="#A0AEC0"/><text x="12" y="20" fontSize="9" fontWeight="700" textAnchor="middle" fill="#fff">FILE</text></svg>
    )
  }

  // 🔥 Audio control functions
  const toggleAudioPlay = (audioId, audioSrc) => {
    const audio = audioRefs.current[audioId]
    if (!audio) return

    if (playingAudioId === audioId) {
      // Pause current audio
      audio.pause()
      setPlayingAudioId(null)
    } else {
      // Stop any currently playing audio
      if (playingAudioId !== null && audioRefs.current[playingAudioId]) {
        audioRefs.current[playingAudioId].pause()
        audioRefs.current[playingAudioId].currentTime = 0
      }
      // Play new audio
      audio.src = audioSrc
      audio.play()
      setPlayingAudioId(audioId)
    }
  }

  const handleAudioTimeUpdate = (audioId) => {
    const audio = audioRefs.current[audioId]
    if (!audio) return
    const progress = (audio.currentTime / audio.duration) * 100
    setAudioProgress(progress)
  }

  const handleAudioLoadedMetadata = (audioId) => {
    const audio = audioRefs.current[audioId]
    if (!audio) return
    setAudioDuration(audio.duration)
  }

  const handleAudioEnded = (audioId) => {
    setPlayingAudioId(null)
    setAudioProgress(0)
    if (audioRefs.current[audioId]) {
      audioRefs.current[audioId].currentTime = 0
    }
  }

  // 🔥 Cleanup audio on unmount
  useEffect(() => {
    return () => {
      Object.keys(audioRefs.current).forEach(key => {
        if (audioRefs.current[key]) {
          audioRefs.current[key].pause()
          audioRefs.current[key].src = ''
        }
      })
    }
  }, [])

 
  let content = null
  let parsed = null
  let allMediaItems = []

  // parse JSON payload
  try {
    if (m && (m.type === 'image' || m.type === 'images' || m.type === 'file')) {
      parsed = (typeof m.message === 'string') ? JSON.parse(m.message) : m.message
    } else if (m && m.type === 'text' && typeof m.message === 'string') {
      const s = m.message.trim()
      if (s && (s.startsWith('{') || s.startsWith('['))) {
        try { parsed = JSON.parse(m.message) } catch (e) { parsed = null }
      }
    }
  } catch (e) {
    parsed = null
  }

  const files = (parsed && parsed.files) || []

  // 🔥 Check file types
  const isImageFile = (f) => {
    if (!f) return false
    if (f.mime && String(f.mime).startsWith('image/')) return true
    if (f.url && (String(f.url).startsWith('data:') || String(f.url).startsWith('blob:'))) return true
    const name = (f.filename || f.originalName || '').toLowerCase()
    return /\.(jpg|jpeg|png|gif|bmp|webp|svg|ico|tiff|tif)$/i.test(name)
  }

  const isVideoFile = (f) => {
    if (!f) return false
    if (f.mime && String(f.mime).startsWith('video/')) return true
    const name = (f.filename || f.originalName || '').toLowerCase()
    return /\.(mp4|webm|mov|avi|mkv|m4v|wmv|flv|f4v|ogv)$/i.test(name)
  }

   // In MessageBubble.jsx, update the isAudioFile function:
const isAudioFile = (f) => {
  if (!f) return false
  if (f.mime && String(f.mime).startsWith('audio/')) return true
  
  // 🔥 Check all possible filename sources
  const name = (f.filename || f.originalName || f.name || '').toLowerCase()
  // Also check URL for filename
  const urlName = (f.url || '').split('/').pop().toLowerCase()
  const fullName = name || urlName
  
  return /\.(mp3|wav|flac|aac|ogg|wma|m4a|m4b|m4p|m4r|amr|awb|3gp)$/i.test(fullName)
}

  // 🔥 Build gallery items from ALL media files
  if (files && files.length) {
    allMediaItems = files.filter(f => f && (
      isImageFile(f) || isVideoFile(f) || isAudioFile(f)
    ))
  }

  // 🔥 Open gallery with ALL media items
  const openGallery = (index) => {
    if (allMediaItems.length > 0) {
      // Stop any playing audio when opening gallery
      if (playingAudioId !== null && audioRefs.current[playingAudioId]) {
        audioRefs.current[playingAudioId].pause()
        audioRefs.current[playingAudioId].currentTime = 0
        setPlayingAudioId(null)
        setAudioProgress(0)
      }
      setGalleryItems(allMediaItems)
      setGalleryIndex(index)
      setGalleryOpen(true)
    }
  }

  if (files && files.length) {
    const imageFiles = files.filter(isImageFile)
    const videoFiles = files.filter(isVideoFile)
    const audioFiles = files.filter(isAudioFile)
    const otherFiles = files.filter(f => !isImageFile(f) && !isVideoFile(f) && !isAudioFile(f))

    content = (
      <div className="mb-files">
        {/* Images */}
        {imageFiles && imageFiles.length > 0 ? (
          <div className="image-grid">
            {imageFiles.map((f, i) => {
              let s = f && f.url ? String(f.url) : ''
              let src = null
              if (s) {
                if (s.startsWith('http') || s.startsWith('data:') || s.startsWith('blob:')) src = s
                else if (s.startsWith('/photo/chat') && IMAGE_CHAT_DOMAIN) src = IMAGE_CHAT_DOMAIN + s
                else src = `${BASE}${s.startsWith('/') ? s : '/' + s}`
              }

              return (
                <div key={i} className="mb-file-item image-cell">
                  <img 
                    onClick={() => openGallery(i)} 
                    src={src} 
                    alt={f.originalName || f.filename} 
                    className="mb-image" 
                  />
                </div>
              )
            })}
          </div>
        ) : null}

        {/* Videos */}
        {videoFiles && videoFiles.length > 0 ? (
          <div className="video-grid">
            {videoFiles.map((f, i) => {
              const mediaIndex = imageFiles.length + i
              let s = f && f.url ? String(f.url) : ''
              let src = null
              if (s) {
                if (s.startsWith('http') || s.startsWith('data:') || s.startsWith('blob:')) src = s
                else if (s.startsWith('/photo/chat') && IMAGE_CHAT_DOMAIN) src = IMAGE_CHAT_DOMAIN + s
                else src = `${BASE}${s.startsWith('/') ? s : '/' + s}`
              }

              return (
                <div key={i} className="mb-file-item video-cell" onClick={() => openGallery(mediaIndex)}>
                  <div className="video-thumbnail">
                    <video 
                      src={src} 
                      muted 
                      playsInline
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onLoadedData={(e) => {
                        try {
                          if (e.target.readyState >= 2) {
                            e.target.currentTime = e.target.duration / 2
                          }
                        } catch(err) {}
                      }}
                    />
                    <div className="video-play-icon">▶</div>
                    <div className="video-duration">🎬</div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : null}
{audioFiles && audioFiles.length > 0 ? (
  <div className="audio-grid">
    {audioFiles.map((f, i) => {
      const mediaIndex = imageFiles.length + videoFiles.length + i
      
      // Build audio URL
      let s = f && f.url ? String(f.url) : ''
      let src = null
      if (s) {
        if (s.startsWith('http') || s.startsWith('data:') || s.startsWith('blob:')) src = s
        else if (s.startsWith('/photo/chat') && IMAGE_CHAT_DOMAIN) src = IMAGE_CHAT_DOMAIN + s
        else src = `${BASE}${s.startsWith('/') ? s : '/' + s}`
      }
      
      return (
        <AudioPlayer
          key={i}
          audioUrl={src}
          fileName={f.originalName || f.filename || 'Audio file'}
          className={isMine ? 'audio-mine' : 'audio-them'}
        />
      )
    })}
  </div>
) : null}

        {/* Other files */}
        {otherFiles && otherFiles.length ? (
          otherFiles.map((f, i) => {
            const getFileExt = (name, mime) => {
              try {
                if (mime) {
                  const m = String(mime).toLowerCase()
                  if (m.includes('pdf')) return 'pdf'
                  if (m.includes('word') || m.includes('msword') || m.includes('officedocument.wordprocessingml')) return 'doc'
                  if (m.includes('excel') || m.includes('spreadsheet') || m.includes('officedocument.spreadsheetml')) return 'xls'
                  if (m.includes('presentation') || m.includes('powerpoint') || m.includes('officedocument.presentationml')) return 'ppt'
                  if (m.includes('heic') || m.includes('heif')) return 'heic'
                }
              } catch(e){}
              try {
                const n = String(name || '').toLowerCase();
                if (n.endsWith('.pdf')) return 'pdf'
                if (n.endsWith('.doc') || n.endsWith('.docx')) return 'doc'
                if (n.endsWith('.xls') || n.endsWith('.xlsx')) return 'xls'
                if (n.endsWith('.ppt') || n.endsWith('.pptx')) return 'ppt'
                if (n.endsWith('.heic') || n.endsWith('.heif')) return 'heic'
              } catch(e){}
              return 'file'
            }

            let s = f && f.url ? String(f.url) : ''
            let src = null
            if (s) {
              if (s.startsWith('http') || s.startsWith('data:') || s.startsWith('blob:')) src = s
              else if (s.startsWith('/photo/chat') && IMAGE_CHAT_DOMAIN) src = IMAGE_CHAT_DOMAIN + s
              else src = `${BASE}${s.startsWith('/') ? s : '/' + s}`
            }

            const ftype = getFileExt(f.originalName || f.filename, f.mime)
            return (
              <div key={i} className="mb-file-item">
                <div className="mb-file-nonimage">
                  <div className="mb-file-icon"><FileIcon type={ftype} /></div>
                  <div className="mb-file-meta">
                    <a href={src || '#'} download={f.originalName || f.filename} target="_blank" rel="noreferrer" className="mb-file-link">{f.originalName || f.filename}</a>
                    <div className="mb-file-type">{ftype.toUpperCase()}</div>
                  </div>
                </div>
              </div>
            )
          })
        ) : null}

        {parsed && parsed.caption ? <div className="mb-caption">{parsed.caption}</div> : null}
      </div>
    )
  } else {
    content = <div className="mb-text">{m && m.message}</div>
  }

  const time = (m && m.created_at) ? String(m.created_at).slice(11,19) : ''

  return (
    <>
      <div className={`message-item ${isMine ? 'mine' : 'them'}`}>
        <div className={`message-bubble ${isMine ? 'message-bubble--mine' : 'message-bubble--them'}`}>
          {!isMine && (m && (m.sender_fullname || m.sender_name || m.sender_username || m.username)) ? <div className="mb-sender">{m.sender_fullname || m.sender_name || m.sender_username || m.username}</div> : null}
          {content}
          <div className="mb-time">{time}</div>
        </div>
        <PhotoPreview open={!!preview} src={(preview && preview.src) || ''} filename={(preview && preview.filename) || ''} caption={(parsed && parsed.caption) || ''} onClose={() => setPreview(null)} />
      </div>

      {/* AutoPlayGallery with ALL media items */}
      <AutoPlayGallery
        items={galleryItems}
        initialIndex={galleryIndex}
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        autoPlaySpeed={3000}
        showControls={true}
        showThumbnails={true}
        showCounter={true}
        loop={true}
      />
    </>
  )
}